// the 'any' in the normalizer is because the data is coming from the API
// brb using zod to give type to api data

import { PostModel } from '@dialogueconsulting/sked-preview'
import { format } from 'date-fns'
import orderBy from 'lodash.orderby'

import type { ConversationTimelineItem } from 'Components/conversation'
import groupPostsByDate from 'utils/groupPostsByDate'

export const normalizeSetting = (setting: any) => {
  return {
    ...setting,
    companyName: setting.company,
    isStatusChangesVisible: setting.isStatusChangesVisible ?? true,
    isExternalNotesVisible: setting.isExternalNotesVisible ?? true,
    isPostActivityVisible: setting.isPostActivityVisible ?? true,
    isConversationsEnabled:
      setting.isConversationsEnabled ??
      ((setting.isStatusChangesVisible ?? true) ||
        (setting.isExternalNotesVisible ?? true) ||
        (setting.isPostActivityVisible ?? true)),
    isReadOnly: setting.isReadOnly,
  }
}

export const mapPlatform = (platform: string) => {
  switch (platform) {
    case 'FB':
      return 'facebook'
    case 'GMB':
      return 'googleBusiness'
    case 'IG':
      return 'instagram'
    case 'LI':
      return 'linkedin'
    case 'PI':
      return 'pinterest'
    case 'SC':
      return 'snapchat'
    case 'TW':
      return 'twitter'
    case 'TT':
      return 'tiktok'
    case 'YT':
      return 'youtube'
    case 'OT':
      return 'multiplePlatforms'
    default:
      return 'multiplePlatforms'
  }
}

export const deMapPlatform = (platform: string) => {
  switch (platform) {
    case 'facebook':
      return 'FB'
    case 'googleBusiness':
      return 'GMB'
    case 'instagram':
      return 'IG'
    case 'linkedin':
      return 'LI'
    case 'pinterest':
      return 'PI'
    case 'snapchat':
      return 'SC'
    case 'twitter':
      return 'TW'
    case 'tiktok':
      return 'TT'
    case 'youtube':
      return 'YT'
    case 'multiplePlatforms':
      return 'OT'
    default:
      return 'OT'
  }
}

export const normalizeDateToRowId = (date: string) =>
  format(new Date(date), 'dd-MM-yyyy')

export const datePlaceholderForDraftPost = '00-00-0000'

export const normalizePosts = (raw: any, isReadOnly: boolean) => {
  const data = groupPostsByDate(raw)

  let postIndex = 1

  let posts = data.map((item: any) => ({
    id:
      item.date === 'drafts'
        ? datePlaceholderForDraftPost
        : normalizeDateToRowId(item.date),
    date:
      item.date === 'drafts'
        ? datePlaceholderForDraftPost
        : format(new Date(item.date), 'EEEE, do MMMM'),
    isVisible: true,
    posts: item.posts.map((post: any) => {
      return {
        postIndex: postIndex++,
        id: post.id,
        status: post.status.type,
        statusLabel: post.status.label,
        time:
          item.date === 'drafts'
            ? datePlaceholderForDraftPost
            : format(new Date(post.when), 'h:mm a'),
        accounts: post.accounts.map((account: any) => ({
          avatar: account.avatar,
          name: account.name,
          avatarBorderColor: account?.color || '#000000',
        })),
        caption: post.caption,
        platform: (() => {
          const isSinglePlatform = post.accounts.every(
            (val: any, _: number, arr: any) =>
              val.platform == arr[arr.length - 1].platform
          )
          const shortFormPlatform = isSinglePlatform
            ? post.accounts[0].platform
            : 'multiplePlatforms'
          return mapPlatform(shortFormPlatform)
        })(),
        media: post.media,
        isUndo: post.status.type !== 'pending',
        isSelected: false,
        isVisible: true,
        when: post.when,
      }
    }),
  }))

  if (isReadOnly) {
    posts = posts.filter((p) => p.date !== '00-00-0000')
  }

  return posts
}

export const normalizeAccounts = (settings: any) => {
  const result: any = []
  settings.accounts.forEach((account: any) => {
    const obj = {
      id: account.id, // use account.name for <AccountSelector />,
      name: account.name,
      avatar: account.avatar,
      platform: mapPlatform(account.platform),
      avatarBorderColor: account.color,
      checked: true,
    }

    if (!account.group) {
      result.push(obj)
    } else {
      const index = result.findIndex((o: any) => o.name === account.group)
      if (index === -1) {
        result.push({
          id: account.group,
          name: account.name,
          checked: true,
          group_options: [obj],
        })
      } else {
        if (!result[index].group_options) {
          result[index].group_options = []
        }
        result[index].group_options.push(obj)
      }
    }
  })

  return result
}

export interface StatusKey {
  pending: string
  approved: string
  requestChange: string
}

export const normalizeStatusKey = (settings: any): StatusKey => {
  return {
    pending: settings.statuses.pending?.key,
    approved: settings.statuses.approved?.key,
    requestChange: settings.statuses.requestChange?.key,
  }
}

// Coerce a possibly object-shaped platform ({ accountId, platformId, platformType })
// to the 2-letter code that sked-preview expects. Strings pass through; empty/missing
// defaults to "IG" (matches the backend default). Some legacy posts carry the object
// shape on platformType, which sked-preview's switch would otherwise reject as
// "unknown platform [object Object]". An object missing platformType also falls back to
// "IG" — acceptable because the affected payloads always carry platformType.
export const toPlatformCode = (value: any): string =>
  (value && typeof value === 'object' ? value.platformType : value) || 'IG'

export const normalizeSkedPreviewPost = (post: any, accounts: any) => {
  // return multiple sked preview as array because a draft post can have multiple platforms
  if (Array.isArray(post)) {
    return post
      .filter((item) => item)
      .map((p) => {
        const account = accounts.find(
          (account: any) => account.id === p?.account_id
        )

        const currentUser = {
          username: account.name,
          profile_picture_url: account.avatar,
        }

        return {
          ...new PostModel(
            p,
            {
              id: account.id,
              profile_picture_url: account.avatar,
              username: account.name,
            },
            undefined,
            undefined
          ),
          platform: toPlatformCode(p.platform),
          accountId: account.id,
          collaboratingIgUsers: getCollaboratingIgUsers(p, currentUser),
        }
      })
  } else {
    const account = accounts.find(
      (account: any) => account.id === post?.account_id
    )

    if (!account) {
      throw new Error('Account not found')
    }
    const currentUser = {
      username: account.name,
      profile_picture_url: account.avatar,
    }

    return {
      ...new PostModel(
        post,
        {
          id: account.id,
          profile_picture_url: account.avatar,
          username: account.name,
        },
        undefined,
        undefined
      ),
      platform: toPlatformCode(post.platform),
      collaboratingIgUsers: getCollaboratingIgUsers(post, currentUser),
    }
  }
}
function getCollaboratingIgUsers(
  p: any,
  currentUser: {
    username: string
    profile_picture_url: string
  }
) {
  return p.collaboratingIgUsers && Array.isArray(p.collaboratingIgUsers)
    ? [
        {
          username: currentUser.username,
          profilePictureUrl: currentUser.profile_picture_url,
        },
        ...p.collaboratingIgUsers,
      ]
    : [
        {
          username: currentUser.username,
          profilePictureUrl: currentUser.profile_picture_url,
        },
      ]
}
const buildInitials = (name?: string): string => {
  if (!name) {
    return '??'
  }

  const initials = name
    .split(' ')
    .filter(Boolean)
    .map((part: string) => part[0]?.toUpperCase())
    .slice(0, 2)
    .join('')

  return initials || name.slice(0, 2).toUpperCase()
}

const toStatusMeta = (status?: any) => {
  if (!status) {
    return null
  }

  return {
    key: status.key,
    type: status.type,
    label: status.label,
    color: status.color,
  }
}

const resolveExternalReviewer = (comment: any) => {
  if (!comment?.metadata?.username) {
    return false
  }

  return comment.metadata.username !== comment?.createdBy?.name
}

export const normalizeMessages = (
  comments: any
): ConversationTimelineItem[] => {
  if (!Array.isArray(comments) || comments.length === 0) {
    return []
  }

  return comments.map((comment: any) => {
    const authorName = comment?.createdBy?.name || 'Unknown user'
    const authorId = comment?.createdBy?.id ?? null

    const base = {
      id: comment.id,
      createdAt: comment.createdAt,
      visibility: comment.visibility ?? 'INTERNAL',
      event: comment.event || comment.type,
      metadata: comment.metadata ?? {},
      author: {
        id: authorId,
        name: authorName,
        avatarUrl: comment?.createdBy?.avatar ?? null,
        initials: buildInitials(authorName),
        isSelf: Boolean(comment.isMessageOwner),
      },
      isSystem: comment.type !== 'UserWritten',
      isMessageOwner: Boolean(comment.isMessageOwner),
    }

    switch (comment.type) {
      case 'StatusChanged': {
        const statusChange = {
          oldStatus: toStatusMeta(comment.oldStatus),
          newStatus: toStatusMeta(comment.newStatus),
        }

        return {
          ...base,
          variant: 'statusChange',
          comment: comment.text || '',
          statusChange,
          isRequestChange: statusChange.newStatus?.type === 'requestChange',
          isExternalReviewer: resolveExternalReviewer(comment),
          indicatorColor: statusChange.newStatus?.color,
        }
      }

      case 'PostTypeChanged': {
        return {
          ...base,
          variant: 'postTypeChange',
          text: comment.text || '',
          postTypeChange: {
            oldPostType: comment?.metadata?.oldPostType ?? null,
            newPostType: comment?.metadata?.newPostType ?? null,
            scheduleTime: comment?.metadata?.scheduleTime ?? null,
          },
          indicatorColor: '#6840f5',
        }
      }

      case 'UserWritten': {
        return {
          ...base,
          variant: 'note',
          text: comment.text || '',
          indicatorColor:
            comment.visibility === 'EXTERNAL' ? '#f6b324' : '#c4c4c4',
        }
      }

      case 'PostCreated':
      default: {
        return {
          ...base,
          variant: 'postCreated',
          text: comment.text || '',
          postCreated: {
            postType: comment?.metadata?.postType ?? null,
            statusLabel:
              comment?.metadata?.statusLabel ??
              comment?.metadata?.status ??
              comment?.newStatus?.label ??
              comment?.oldStatus?.label ??
              null,
          },
          indicatorColor: '#9ca3af',
        }
      }
    }
  })
}

export const normalizeRepliedMessage = (
  data: any
): ConversationTimelineItem => {
  const authorName = data?.createdBy?.name || 'Unknown user'

  return {
    id: data.id,
    createdAt: data.createdAt,
    visibility: data.visibility ?? 'INTERNAL',
    variant: 'statusChange',
    event: data.event || data.type || 'POST_STATUS_CHANGE',
    metadata: data.metadata ?? {},
    author: {
      id: data?.createdBy?.id ?? null,
      name: authorName,
      avatarUrl: data?.createdBy?.avatar ?? null,
      initials: buildInitials(authorName),
      isSelf: true,
    },
    isSystem: true,
    isMessageOwner: true,
    comment: data.text || '',
    statusChange: {
      oldStatus: toStatusMeta(data.oldStatus),
      newStatus: toStatusMeta(data.newStatus),
    },
    isRequestChange: data?.newStatus?.type === 'requestChange',
    indicatorColor: data?.newStatus?.color,
    isExternalReviewer: resolveExternalReviewer(data),
  }
}

export const isPostIsStory = (post: any) =>
  ((post.isStory || post.type === 'story') && !post.storyType) ||
  ((post.isStory || post.type === 'story') &&
    post.storyType &&
    post.storyType === 'story')

export const normalizeGridPreview = (data: any, selectedPostId: string) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return {
      account: {
        name: '',
        handler_name: '',
        avatar: '',
      },
      posts: [],
    }
  }
  const { name, avatar } = data[0].accounts[0]
  const result = data
    .map((post: any) => {
      return {
        id: post.id,
        status: post.isPosted
          ? 'posted'
          : post.isPublishing
            ? 'publishing'
            : post.status.type,
        media: post.media,
        isSelected: post.id === selectedPostId,
      }
    })
    .filter((post: any) => !isPostIsStory(post))

  return {
    account: {
      name,
      handler_name: '',
      avatar,
    },
    posts: orderBy(result, 'isSelected', 'desc'),
  }
}

export const normalizeGridPreviewAllPosts = (data: any) => {
  return data.map((post: any) => ({
    accounts: post.accounts.map((account: any) => account.id),
    accountInfo: {
      avatar: post.accounts.find((_: any, index: any) => index === 0).avatar,
      name: post.accounts.find((_: any, index: any) => index === 0).name,
      handler_name: '',
    },
    id: post.id,
    status: post.isPosted ? 'posted' : post.status.type,
    media: post.media,
  }))
}

export const normalizeCalendarEvents = (data: any) => {
  return data.map((post: any) => ({
    id: post.id,
    start: new Date(post.when),
    end: new Date(post.when),
    tileData: {
      platform: mapPlatform(post.accounts[0]?.platform),
      status: post.status.type,
      mediaSrc: post.media.src,
      time: format(new Date(post.when), 'h:mm a'),
      type: post.media.type,
      caption: post.caption,
      hasNotification: post.hasNotification,
    },
  }))
}
